import type {WasteRules} from '@artemis-group/aiw-contracts';
/**
 * proposal-pricing.ts — the CRM's read of the pricing engine.
 *
 * The engine is the domain package, called by `services/api` (`/api/pricing-base`,
 * `/api/pricing-base/preview`). Nothing here computes a price: this file fetches,
 * types the answers, and holds the two unit rules the screen needs.
 *
 *   · Carpet is costed in LM and quoted to the customer in m² of FLOOR. Staff must
 *     therefore give both a roll length and an area; every other floor is one number.
 *   · A colour the customer has not chosen is `Customer not yet decided` — the same
 *     words on both copies (Jacky, 2026-09-16).
 */
import {env} from './env';
import {authHeaders,fetchImpl} from './api';

export type Variant = {
  id: string; product_id: string; name?: string | null; display_name?: string | null;
  colour?: string | null; line_type?: string | null; label?: string | null;
  is_default: boolean | null; mat_cost: number | null; price_per_lm?: number | null;
  width?: number | null; thickness?: number|string|null; uom?: string | null; pattern?: string | null; supplier_name?: string | null;
  supplier_id?: string | null;
  /** Builder-only / on-request colour (26 Oct 2026): listed under "Special order", never by default. */
  special_order?: boolean | null;
};
export type ReviewProduct = {
  id: string; name: string; type: string | null; sub_type: string | null; uom: string | null;
  supplier_name: string | null; standard_cost: number | null;
  supplier_id?: string | null;
  /** The brand the customer knows (National Select, PACO, Topdeck…). PACO is the supplier for both PACO and
   *  National Select ranges, so `supplier_name` is not the brand. Absent on an older API: fall back to it. */
  brand?: string | null;
  /** How the product is laid — "plank", "herringbone", "chevron". The waste allowance follows it. */
  parquet_form?: string | null;
  /**
   * WHAT THIS PRODUCT COMES WITH — the catalogue's own accessory bindings.
   *
   * Jacky, 19 Sep: *"accessorie matching? and type product ID? I am not a machine. shouldnt that be tick
   * inside the product instead."* These are that tick: the accessory's name, its unit and how many of it
   * one unit of floor takes (0.5 = one nosing bar covers two steps).
   */
  accessories?: {type: string; name: string; variant_id: string | null; variant_name: string | null; unit: string | null; qty_per_unit: number | null; note: string | null}[];
  pricing: {
    cost: number | null; costRange: { min: number; max: number } | null; unit: 'm²' | 'LM';
    source: string; issues: string[]; missing: string[]; manual: boolean; rule: string | null;
    promotion: boolean; priceBasis: string | null; colours: number;
  };
  variants: Variant[];
  /** What the engine agrees a customer may choose. Decided in the engine, not here. */
  colours: Variant[];
};
export type InstallRule = { id: string; name: string; unit: 'm²' | 'LM'; labour: number | null; glue: number | null; glued: boolean; underlay: number | null; upgrade: number | null; markup: number | null };
export type BaseRate = { id: string; name: string; unit: string; sell: number | null; material: number | null; labour: number | null; disposalSell?: number | null; weight?: number | null; dumpingRate?: number | null; notes: string };
export type PricingModel = { version: 1; currency: 'AUD'; gstRate: number; rounding?: { totalTo?: number; mode?: 'down' | 'nearest' | 'up' }; rates: BaseRate[]; installation: InstallRule[] };
export type PricedLine = { label: string; unit: string; quantity: number; revenue: number | null; material: number | null; labour: number | null; warnings: string[]; revenueRange?: { min: number; max: number }; notices?: string[]; costBasis?: string | null };
export type PricedResult = {
  version: 1; gstRate?:number; lines: PricedLine[]; warnings: string[];
  /** Present on the preview response: the option pool priced at the proposal's rate, and the rate
   *  itself, so the screen can label what the pool is listed at. */
  options?: PricedLine[]; margin?: {kind: 'gm' | 'markup'; value: number} | null; optionCount?: number;
  /** The assisted rate's two margins, computed by the API from the engine's own internal figures:
   *  external installer first (the one that counts), Artemis crew as the bonus. Declared structurally
   *  here: the CRM reads the API's answer, it does not import the API's code. */
  gm?: GmScenarioSet | null;
  subtotal: number | null; gst: number | null; total: number | null;
  subtotalRange: { min: number; max: number } | null; settledRange: { min: number; max: number } | null;
  internal: { material: number | null; externalLabour: number | null; artemisLabour: number; artemisContribution: number | null; externalContribution: number | null };
};

/** One scenario: what the quote earns when the labour is paid out, or when the crew does it. */
export type GmScenario = {scenario: 'external_install' | 'artemis_crew'; sell: number | null; contribution: number | null; gm: number | null};
export type GmScenarioSet = {external: GmScenario; artemisCrew: GmScenario; bonus: number | null; lines: ({label: string; sell: number | null} & {external: GmScenario; artemisCrew: GmScenario; bonus: number | null})[]};

/** One accessory family the floor can come with — the switch, and the TYPES inside it. */
export type AccessoryType = {
  product_id: string;
  /** underlay | moisture_barrier | scotia | trims | adhesive | levelling | stair */
  kind: string;
  name: string;
  supplier_name: string | null;
  supplier_id: string | null;
  unit: string | null;
  variants: {variant_id: string; label: string; unit: string | null; cost: number | null; is_default: boolean;supplier_id:string|null;supplier_name:string|null;length_mm:number|null;spec:string|null;source:string|null}[];
};

export type PricingBase = {
  model: PricingModel;
  products: ReviewProduct[];
  /**
   * THE ACCESSORY CATALOGUE — what a floor can come with.
   *
   * Jacky, 19 Sep, on O-00000004: *"wont allow adding trims or able selecting type of underlay or
   * moisture barrier under the product switch like previous platform"*. The old platform's switchboard
   * listed the underlays, the moisture barrier, the scotia and the four trim families under each floor,
   * each with its supplier/spec options. The face had no such list because this route never sent one.
   */
  accessories?: AccessoryType[] | null;
  /** The allowance rules from the `artemis_services` config — read with the catalogue, never baked in. */
  waste?: WasteRules | null;
  /**
   * THE STAIR WORK AS THE CATALOGUE PRICES IT.
   *
   * Jacky, 19 Sep: *"if you migrated stair work rule from platform, this should be auto from the product"*.
   * Per step, from the product rows the old platform sells ($130 hard floor, $15 carpet, $130 levelling),
   * with the rule's own quantities: nosing via the accessory matching, extra planks at 0.75 m² per step.
   */
  stair?: {
    hard_floor: {product_id: string; name: string; unit: string; sell: number | null};
    carpet: {product_id: string; name: string; unit: string; sell: number | null};
    levelling: {product_id: string; name: string; unit: string; sell: number | null};
    nosing: {product_id: string; name: string; unit: string};
    extra_planks: {product_id: string; name: string; unit: string; per_step: number};
  } | null;
  /** Removal by existing floor (16 Oct 2026): the catalogue's floors and each one's rate per m². */
  removal?: {floor: string; variant_id: string; name: string; sell: number | null}[];
};

/** Staff-only. Reads the configured model and the priced catalogue together, so a
 *  product with no sellable price arrives with the reason already attached. */
export async function loadPricingBase(): Promise<PricingBase> {
  const r = await fetchImpl(`${env.apiBase}/api/pricing-base`, { headers: await authHeaders() });
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Pricing is unavailable');
  const products = (body.products ?? body.data?.products ?? []) as ReviewProduct[];
  const accessories = (body.accessories ?? body.data?.accessories ?? null) as AccessoryType[] | null;
  const waste = (body.waste ?? body.data?.waste ?? null) as WasteRules | null;
  const stair = (body.stair ?? body.data?.stair ?? null) as PricingBase['stair'];
  const removal = (body.removal ?? body.data?.removal ?? []) as NonNullable<PricingBase['removal']>;
  const model = (body.model ?? body.data?.model) as PricingModel | undefined;
  if (!model) throw Error('Pricing configuration is unavailable');
  return { model, products, accessories, waste, stair, removal };
}

/** Staff-only, and the only way a figure reaches the screen. */
export async function previewPricing(input: unknown): Promise<PricedResult> {
  const r = await fetchImpl(`${env.apiBase}/api/pricing-base/preview`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'This selection could not be priced');
  return {...(body.data??body),options:body.options??body.data?.options,margin:body.margin??body.data?.margin,optionCount:body.optionCount??body.data?.optionCount,gm:body.gm??body.data?.gm} as PricedResult;
}

/** Carpet is the only floor the engine costs by roll length. */
export const isCarpet = (unit: string | null | undefined) => unit === 'LM';

/** The colour as the customer and the sample name it. Empty names fall back to the
 *  undecided sentence rather than showing an identifier. */
export function colourName(v: Variant): string {
  return (v.colour ?? v.display_name ?? v.name ?? v.label ?? '').trim() || 'Colour not named';
}

/** Floor area a set of chosen rooms adds up to, and the rooms that cannot answer. */
export function roomsArea(rooms: { name: string; area: number | null }[], chosen: string[]) {
  const picked = rooms.filter(r => chosen.includes(r.name));
  const known = picked.filter(r => (r.area ?? 0) > 0);
  return { area: known.reduce((n, r) => n + (r.area ?? 0), 0), unknown: picked.filter(r => !((r.area ?? 0) > 0)).map(r => r.name), count: picked.length };
}
